import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import DOMPurify from 'dompurify';
import GamesSection from './GamesSection';
import DevicesCard from './profile/DevicesCard';

const UserProfile = ({ API, userToken, onLogout, isDarkMode, onToggleDarkMode, isAdmin = false, initialTab = "dashboard", appSettings = {}, onUserUpdate, setCurrentView, onGameActive, highlightPlanId = null, onHighlightDone = null, userPlan = null }) => {
  
  const { lang, setLang, t, LANGUAGES } = useTranslation();
  // App-shell caches: read synchronously so /profile, /profile?tab=membership,
  // and /orders all render cached chrome from frame 1 instead of the loading
  // skeleton. Each fetch silently refreshes the cache after.
  const _readUpCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _rawCachedProfile = _readUpCache('upProfileDataCache');
  // Only use cached profile if it has the plan field — otherwise we'd briefly
  // render "No Plan" for paid users until the fresh /user/profile fetch resolves.
  const _cachedProfileData = (_rawCachedProfile && _rawCachedProfile.plan) ? _rawCachedProfile : null;
  const _cachedMembershipPlans = _readUpCache('upMembershipPlansCache');
  const _cachedOrders = _readUpCache('upOrdersCache');
  const _cachedAnnouncements = _readUpCache('upAnnouncementsCache');
  const _cachedMessages = _readUpCache('upMessagesCache');
  const _cachedAvatars = _readUpCache('upAvatarsCache');

  const [selectedLang, setSelectedLang] = React.useState(lang);
  const [activeTab, setActiveTab] = React.useState(initialTab);
  const [profileData, setProfileData] = React.useState(_cachedProfileData);
  // isMounting: 100ms delay to prevent the login form from flashing during auth.
  // Skip the delay when we have ANY cached content (profile / help / staticPages)
  // — auth state is already established or content is ready from cache.
  const _hasAnyCachedContent = !!_cachedProfileData
    || (typeof window !== 'undefined' && !!localStorage.getItem(`upHelpContentCache_${lang}`))
    || (typeof window !== 'undefined' && !!localStorage.getItem('upStaticPageCache_about'))
    || (typeof window !== 'undefined' && !!localStorage.getItem('upStaticPageCache_terms'))
    || (typeof window !== 'undefined' && !!localStorage.getItem('upStaticPageCache_privacy'))
    || (typeof window !== 'undefined' && !!localStorage.getItem('upStaticPageCache_help'));
  const [isMounting, setIsMounting] = React.useState(!_hasAnyCachedContent);
  // Skip the loading skeleton when cached profile exists
  const [loading, setLoading] = React.useState(!_cachedProfileData);
  const [avatars, setAvatars] = React.useState(_cachedAvatars || []);
  const [avatarGenders, setAvatarGenders] = React.useState([]);
  const [announcements, setAnnouncements] = React.useState(_cachedAnnouncements || []);
  const [messages, setMessages] = React.useState(_cachedMessages || []);
  const [membershipPlans, setMembershipPlans] = React.useState(_cachedMembershipPlans || []);
  const [orders, setOrders] = React.useState(_cachedOrders || []);
  const [ordersPage, setOrdersPage] = React.useState(1);
  const [ordersYearFilter, setOrdersYearFilter] = React.useState('all');
  const ORDERS_PER_PAGE = 12;
  const [paymentDisclaimer, setPaymentDisclaimer] = React.useState({ enabled: false, text: '' });
  const [editMode, setEditMode] = React.useState(false);
  const [showClearCacheModal, setShowClearCacheModal] = React.useState(false);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [editData, setEditData] = React.useState({ 
    full_name: '', 
    email: '', 
    current_password: '', 
    new_password: '',
    subject: '',
    message: ''
  });
  const [notification, setNotification] = React.useState({ show: false, message: '', type: '' });
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [messageToDelete, setMessageToDelete] = React.useState(null);
  const [replyingTo, setReplyingTo] = React.useState(null);
  const [replyContent, setReplyContent] = React.useState('');
  const [loadingPurchase, setLoadingPurchase] = React.useState(false);
  const [paymentProviders, setPaymentProviders] = React.useState({ stripe_enabled: false, paypal_enabled: false });
  const [showPaymentMethodModal, setShowPaymentMethodModal] = React.useState(null); // null or {planId, planName}
  const [unreadCount, setUnreadCount] = React.useState(0);
  // Help content + static pages: cached so /help/documentation, /profile?tab=about,
  // /profile?tab=terms, /profile?tab=privacy render content from frame 1 instead
  // of the centered "Loading..." spinner. helpContent is per-lang, static pages
  // are language-neutral admin HTML.
  const [helpContent, setHelpContent] = React.useState(() => {
    try { const c = localStorage.getItem(`upHelpContentCache_${lang}`); if (c) return JSON.parse(c); } catch {}
    return null;
  });
  const [loadingHelpContent, setLoadingHelpContent] = React.useState(false);
  const [userDeletionRequest, setUserDeletionRequest] = React.useState(null);
  const [staticPages, setStaticPages] = React.useState(() => {
    const init = { about: null, terms: null, privacy: null, help: null };
    try {
      ['about', 'terms', 'privacy', 'help'].forEach(k => {
        const c = localStorage.getItem(`upStaticPageCache_${k}`);
        if (c) init[k] = JSON.parse(c);
      });
    } catch {}
    return init;
  });
  const [loadingStaticPage, setLoadingStaticPage] = React.useState(false);
  
  // User Settings & Goals states
  const [userSettings, setUserSettings] = React.useState({
    daily_word_goal: 3,
    weekly_practice_minutes: 30,
    email_notifications: true,
    learning_reminders: true,
    achievement_notifications: true,
    subscription_reminders: true,
    learning_time: 'morning',
    difficulty_level: 'beginner',
    audio_autoplay: true,
    weather_enabled: true,
    weather_city: 'Casablanca'
  });
  const [featureFlags, setFeatureFlags] = React.useState({
    learning_goals: { enabled: true, premium: false },
    notification_settings: { enabled: true, premium: false }
  });
  // Gates the Notification Settings section so admin-hidden items don't flicker on initial paint
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = React.useState(false);
  const [todayGoals, setTodayGoals] = React.useState({
    daily_word_goal: 3,
    words_learned_today: 0,
    weekly_practice_goal: 30,
    practice_minutes_today: 0
  });
  const [savingSettings, setSavingSettings] = React.useState(false);

  // Update active tab when initialTab prop changes
  React.useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Scroll to + briefly highlight a plan card when arriving from
  // /login?plan=<id> via the SSR landing's "Get Started" buttons.
  // Cleared via onHighlightDone so a refresh doesn't re-trigger.
  React.useEffect(() => {
    if (!highlightPlanId || activeTab !== 'membership' || membershipPlans.length === 0) return;
    const id = setTimeout(() => {
      const card = document.querySelector(`[data-plan-id="${highlightPlanId}"]`);
      if (!card) {
        if (onHighlightDone) onHighlightDone();
        return;
      }
      try { card.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
      // Pulse ring for ~2.5s, then clear
      card.classList.add('ring-4', 'ring-blue-500', 'ring-offset-2');
      setTimeout(() => {
        card.classList.remove('ring-4', 'ring-blue-500', 'ring-offset-2');
        if (onHighlightDone) onHighlightDone();
      }, 2500);
    }, 250); // small delay so DOM has rendered the cards
    return () => clearTimeout(id);
  }, [highlightPlanId, activeTab, membershipPlans, onHighlightDone]);

  // Prevent login form flash during mount
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounting(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    if (userToken) {
      loadProfile();
      loadAvatars();
      loadAvatarGenders();
      loadAnnouncements();
      loadMessages();
      loadMembershipPlans();
      loadPaymentDisclaimer();
      loadPaymentProviders();
      loadOrders();
      loadWeatherSettings();
    }
  }, [userToken]);

  // Auto-reload messages when Messages tab is active
  React.useEffect(() => {
    if (activeTab === "messages" && userToken) {
      setUnreadCount(0); // Clear unread count when viewing messages
      loadMessages();
    }
  }, [activeTab]);

  // Auto-reload orders when Orders tab is active
  React.useEffect(() => {
    if (activeTab === "orders" && userToken) {
      loadOrders();
    }
  }, [activeTab, userToken]);

  // Handle payment return (Stripe/PayPal redirect back)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');

    if (paymentStatus === 'success') {
      showNotification('Payment successful! Your subscription is now active.', 'success');
      loadProfile();
      loadOrders();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      showNotification('Payment was cancelled. No charges were made.', 'info');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'paypal-success') {
      // PayPal returns here after approval — need to capture the payment
      const paypalOrderId = sessionStorage.getItem('paypal_order_id');
      if (paypalOrderId && userToken) {
        (async () => {
          setLoadingPurchase(true);
          try {
            const response = await fetch(`${API}/capture-paypal-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userToken}`
              },
              body: JSON.stringify({ order_id: paypalOrderId })
            });
            const data = await response.json();
            if (response.ok) {
              showNotification('Payment successful! Your subscription is now active.', 'success');
              loadProfile();
              loadOrders();
            } else {
              showNotification(data.detail || 'Payment capture failed', 'error');
            }
          } catch (error) {
            showNotification('Failed to confirm payment. Please contact support.', 'error');
          } finally {
            setLoadingPurchase(false);
            sessionStorage.removeItem('paypal_order_id');
            sessionStorage.removeItem('paypal_plan_id');
            window.history.replaceState({}, '', window.location.pathname);
          }
        })();
      }
    }
  }, []);

  // Load help content when Help tab is active
  React.useEffect(() => {
    if (activeTab === "help" || activeTab === "about") {
      loadHelpContent();
      // Check if user has a pending deletion request
      if (userToken) {
        fetch(`${API}/user/delete-request`, {
          headers: { 'Authorization': `Bearer ${userToken}` }
        }).then(res => res.ok ? res.json() : null)
          .then(data => setUserDeletionRequest(data))
          .catch(() => {});
      }
    }
  }, [activeTab, lang]);

  // Load static pages when their tabs are active
  // Note: 'help' is loaded via loadHelpContent() (different endpoint with rich content),
  // so it's intentionally NOT included here.
  React.useEffect(() => {
    if (activeTab === "about") {
      loadStaticPage('about');
    } else if (activeTab === "terms") {
      loadStaticPage('terms');
    } else if (activeTab === "privacy") {
      loadStaticPage('privacy');
    }
  }, [activeTab]);

  // Load feature flags on mount and when settings tab is viewed (picks up admin changes)
  React.useEffect(() => {
    loadFeatureFlags();
  }, [activeTab]);

  React.useEffect(() => {
    loadFeatureFlags();
    if (userToken) {
      loadTodayGoals();
    }
  }, [userToken]);

  // Load user settings from profile data (merge, don't overwrite weather settings)
  React.useEffect(() => {
    if (profileData && profileData.settings) {
      setUserSettings(prev => ({
        ...prev,
        ...profileData.settings,
        // Preserve weather settings if they were already loaded
        weather_enabled: prev.weather_enabled,
        weather_city: prev.weather_city
      }));
    }
  }, [profileData]);

  // Auto-poll for new messages every 10 seconds
  React.useEffect(() => {
    if (!userToken) return;
    
    const pollMessages = setInterval(() => {
      loadMessages();
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(pollMessages); // Cleanup on unmount
  }, [userToken, activeTab]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const loadProfile = async () => {
    try {
      const response = await fetch(`${API}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();

        // Calculate hasPaidPlan based on plan name
        const hasPaidPlan = data.plan && data.plan.name !== 'free' && data.plan.name !== 'Free Plan';

        const merged = { ...data, hasPaidPlan: hasPaidPlan };
        setProfileData(merged);
        try { localStorage.setItem('upProfileDataCache', JSON.stringify(merged)); } catch {}
        setEditData({
          full_name: data.full_name,
          email: data.email,
          current_password: '',
          new_password: '',
          subject: '',
          message: ''
        });

        // Update currentUser in App.js when profile is loaded (especially avatar changes)
        if (onUserUpdate) {
          onUserUpdate(data);
        }
      } else {
        console.error('UserProfile: API request failed:', response.status);
      }
    } catch (error) {
      console.error('UserProfile: Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvatars = async () => {
    try {
      const response = await fetch(`${API}/avatars`);
      if (response.ok) {
        const data = await response.json();
        setAvatars(data);
        try { localStorage.setItem('upAvatarsCache', JSON.stringify(data)); } catch {}
      }
    } catch (error) {
      console.error('Error loading avatars:', error);
    }
  };

  const loadAvatarGenders = async () => {
    try {
      const response = await fetch(`${API}/avatar-genders`);
      if (response.ok) {
        const data = await response.json();
        setAvatarGenders(data);
      }
    } catch (error) {
      console.error('Error loading avatar genders:', error);
    }
  };

  // Load user's weather settings
  const loadWeatherSettings = async () => {
    if (!userToken) return;
    try {
      const response = await fetch(`${API}/user/weather-settings`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserSettings(prev => ({
          ...prev,
          weather_enabled: data.weather_enabled !== false,
          weather_city: data.weather_city || 'Casablanca'
        }));
      }
    } catch (error) {
      console.error('Error loading weather settings:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const response = await fetch(`${API}/announcements`);
      if (response.ok) {
        const data = await response.json();
        const arr = Array.isArray(data) ? data : [];
        setAnnouncements(arr);
        try { localStorage.setItem('upAnnouncementsCache', JSON.stringify(arr)); } catch {}
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await fetch(`${API}/messages/inbox`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        try { localStorage.setItem('upMessagesCache', JSON.stringify(data)); } catch {}
        
        // Count unread messages (messages with admin replies that user hasn't opened)
        // If user is not on messages tab, count messages with replies
        if (activeTab !== 'messages') {
          const unread = data.filter(msg => 
            msg.replies && msg.replies.length > 0 && 
            msg.replies.some(reply => reply.from === 'admin')
          ).length;
          setUnreadCount(unread);
        } else {
          // User is viewing messages, mark as read
          console.log('[DEBUG] On messages tab, clearing unread count');
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadMembershipPlans = async () => {
    try {
      const response = await fetch(`${API}/membership/plans`);
      if (response.ok) {
        const data = await response.json();
        setMembershipPlans(data); // Backend already filters active plans
        try { localStorage.setItem('upMembershipPlansCache', JSON.stringify(data)); } catch {}
      }
    } catch (error) {
      console.error('Error loading membership plans:', error);
    }
  };

  const loadPaymentDisclaimer = async () => {
    try {
      const response = await fetch(`${API}/membership/payment-disclaimer`);
      if (response.ok) {
        const data = await response.json();
        setPaymentDisclaimer(data);
      }
    } catch (error) {
      console.error('Error loading payment disclaimer:', error);
    }
  };

  const loadPaymentProviders = async () => {
    try {
      const response = await fetch(`${API}/membership/payment-providers`);
      if (response.ok) {
        setPaymentProviders(await response.json());
      }
    } catch (error) {
      console.error('Error loading payment providers:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await fetch(`${API}/user/orders`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
        try { localStorage.setItem('upOrdersCache', JSON.stringify(data)); } catch {}
      } else {
        console.error('LoadOrders: Failed:', response.status, await response.text());
      }
    } catch (error) {
      console.error('LoadOrders: Error loading orders:', error);
    }
  };

  // Payment Settings Functions
  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/payment-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  const savePaymentSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/payment-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(paymentSettings)
      });
      if (response.ok) {
        showNotification && showNotification('Payment settings saved successfully!', 'success');
      } else {
        showNotification && showNotification('Failed to save payment settings', 'error');
      }
    } catch (error) {
      console.error('Error saving payment settings:', error);
      showNotification && showNotification('Error saving payment settings', 'error');
    }
  };

  const handlePaymentToggle = (provider, enabled) => {
    setPaymentSettings(prev => ({
      ...prev,
      [provider]: { ...prev[provider], enabled }
    }));
  };

  const handlePaymentSettingChange = (provider, field, value) => {
    setPaymentSettings(prev => ({
      ...prev,
      [provider]: { ...prev[provider], [field]: value }
    }));
  };

  const loadHelpContent = async () => {
    setLoadingHelpContent(true);
    try {
      const response = await fetch(`${API}/help-content/${lang}`);
      if (response.ok) {
        const data = await response.json();
        setHelpContent(data);
        try { localStorage.setItem(`upHelpContentCache_${lang}`, JSON.stringify(data)); } catch {}
      } else {
        console.error('Failed to load help content:', response.status);
      }
    } catch (error) {
      console.error('Error loading help content:', error);
    } finally {
      setLoadingHelpContent(false);
    }
  };

  const loadStaticPage = async (pageType) => {
    setLoadingStaticPage(true);
    try {
      const response = await fetch(`${API}/static-pages/${pageType}`);
      if (response.ok) {
        const data = await response.json();
        setStaticPages(prev => ({ ...prev, [pageType]: data }));
        try { localStorage.setItem(`upStaticPageCache_${pageType}`, JSON.stringify(data)); } catch {}
      } else if (response.status !== 404) {
        // 404 just means the admin hasn't created this page yet — not an error
        console.error('Failed to load static page:', response.status);
      }
    } catch (error) {
      console.error('Error loading static page:', error);
    } finally {
      setLoadingStaticPage(false);
    }
  };

  // Load feature flags (public endpoint, no auth needed)
  const loadFeatureFlags = async () => {
    try {
      const response = await fetch(`${API}/features/status`);
      if (response.ok) {
        const data = await response.json();
        setFeatureFlags(data);
      }
    } catch (error) {
      console.error('Error loading feature flags:', error);
    } finally {
      // Mark loaded even on failure — defaults stay applied, but at least one render cycle settles
      setFeatureFlagsLoaded(true);
    }
  };
  
  

  // Load today's goals progress
  const loadTodayGoals = async () => {
    try {
      const response = await fetch(`${API}/user/goals/today`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTodayGoals(data);
      }
    } catch (error) {
      console.error('Error loading today goals:', error);
    }
  };
  
  
  

  // Save user settings
  const saveUserSettings = async (settingsData) => {
    setSavingSettings(true);
    try {
      const formData = new FormData();
      Object.keys(settingsData).forEach(key => {
        formData.append(key, settingsData[key]);
      });
      
      const response = await fetch(`${API}/user/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${userToken}`
        },
        body: formData
      });
      
      if (response.ok) {
        // Update local state
        setUserSettings(prev => ({ ...prev, ...settingsData }));
        // Reload goals if goals were updated
        if (settingsData.daily_word_goal || settingsData.weekly_practice_minutes) {
          loadTodayGoals();
        }
        return { success: true, message: 'Settings saved successfully!' };
      } else {
        const error = await response.json();
        return { success: false, message: error.detail || 'Failed to save settings' };
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      return { success: false, message: 'Failed to save settings' };
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAvatarSelect = async (avatarId) => {
    try {
      const formData = new FormData();
      formData.append('avatar_id', avatarId);
      formData.append('token', userToken);
      
      const response = await fetch(`${API}/user/avatar`, {
        method: 'PUT',
        body: formData
      });
      
      if (response.ok) {
        loadProfile();
      } else {
        showNotification('Failed to update avatar', 'error');
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      showNotification('Failed to update avatar', 'error');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('token', userToken);
      if (editData.full_name !== profileData.full_name) formData.append('full_name', editData.full_name);
      // Email changes are blocked for security - do not send email updates
      if (editData.new_password) {
        formData.append('current_password', editData.current_password);
        formData.append('new_password', editData.new_password);
      }
      
      const response = await fetch(`${API}/user/profile`, {
        method: 'PUT',
        body: formData
      });
      
      if (response.ok) {
        showNotification('Profile updated successfully!');
        setEditMode(false);
        loadProfile();
        setEditData({ ...editData, current_password: '', new_password: '' });
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('Failed to update profile', 'error');
    }
  };

  const handleReplySubmit = async (messageId) => {
    if (!replyContent.trim()) {
      showNotification('Please enter a reply', 'error');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', replyContent);
      formData.append('token', userToken);

      const response = await fetch(`${API}/user/messages/${messageId}/reply`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        showNotification('Reply sent successfully!');
        setReplyingTo(null);
        setReplyContent('');
        // Small delay to ensure backend saves the reply
        setTimeout(() => {
          loadMessages();
        }, 500);
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to send reply', 'error');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      showNotification('Failed to send reply', 'error');
    }
  };

  const handlePurchasePlan = async (planId, planName) => {
    // Prevent double-click
    if (loadingPurchase) return;

    // Check if user already has this plan active
    const currentPlanId = profileData.subscription?.plan_id || profileData.plan?.id;
    if (currentPlanId === planId && profileData.subscription?.status === 'active' && !profileData.subscription?.will_cancel) {
      showNotification('You already have this plan active', 'error');
      return;
    }

    const { stripe_enabled, paypal_enabled } = paymentProviders;

    // If both providers available, show selection modal
    if (stripe_enabled && paypal_enabled) {
      setShowPaymentMethodModal({ planId, planName });
      return;
    }

    // If only one provider, go directly
    if (stripe_enabled) {
      await startStripeCheckout(planId);
    } else if (paypal_enabled) {
      await startPayPalCheckout(planId);
    } else {
      showNotification('No payment provider is configured. Please contact the admin.', 'error');
    }
  };

  const startStripeCheckout = async (planId) => {
    setLoadingPurchase(true);
    setShowPaymentMethodModal(null);
    try {
      showNotification('Redirecting to Stripe checkout...', 'info');
      const response = await fetch(`${API}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ plan_id: planId })
      });
      const data = await response.json();
      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        showNotification(data.detail || 'Failed to create checkout session', 'error');
      }
    } catch (error) {
      showNotification('Failed to start checkout. Please try again.', 'error');
    } finally {
      setLoadingPurchase(false);
    }
  };

  const startPayPalCheckout = async (planId) => {
    setLoadingPurchase(true);
    setShowPaymentMethodModal(null);
    try {
      showNotification('Redirecting to PayPal...', 'info');
      const response = await fetch(`${API}/create-paypal-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ plan_id: planId })
      });
      const data = await response.json();
      if (response.ok && data.approval_url) {
        // Store order_id for capture after PayPal redirect
        sessionStorage.setItem('paypal_order_id', data.order_id);
        sessionStorage.setItem('paypal_plan_id', planId);
        window.location.href = data.approval_url;
      } else {
        showNotification(data.detail || 'Failed to create PayPal order', 'error');
      }
    } catch (error) {
      showNotification('Failed to start PayPal checkout. Please try again.', 'error');
    } finally {
      setLoadingPurchase(false);
    }
  };

  if (loading) {
    return (
      <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <div className="animate-pulse">
          <div className={`h-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/3 mb-4`}></div>
          <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-2/3`}></div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return null;
  }

  const tabs = [
    { id: "dashboard", label: t('dashboard'), icon: "📊" },
    { id: "profile", label: t('profile'), icon: "👤" },
    { id: "membership", label: t('membership'), icon: "💳" },
    { id: "orders", label: t('orders'), icon: "🛍️" },
    { id: "help", label: t('help'), icon: "❓" },
    { id: "about", label: t('about'), icon: "ℹ️" },
    { id: "terms", label: t('terms_of_use'), icon: "📜" },
    { id: "privacy", label: t('privacy_policy'), icon: "🔒" }
  ];

  return (
    <>
      {/* Prevent login form flash during mount */}
      {isMounting ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-2xl">Loading...</div>
        </div>
      ) : (
        <div className="space-y-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          notification.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Profile Header Card - Hidden on Games tab */}
      {activeTab !== 'games' && (
        <div className={`p-6 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <div className="flex items-center gap-4">
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full w-24 h-24 flex items-center justify-center overflow-hidden`} style={{width:96,height:96,flexShrink:0}}>
              {(() => {
                const raw = profileData.avatar?.image_url;
                const baseUrl = API.replace(/\/api$/, '');
                const imgUrl = raw?.startsWith('/') ? `${baseUrl}${raw}` : raw?.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
                return imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={profileData.avatar.name || 'Avatar'}
                    width="96" height="96"
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null;
              })()}
              <span className={`text-6xl ${profileData.avatar?.image_url ? 'hidden' : 'flex'}`}>
                👤
              </span>
            </div>
            <div className="flex-1">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {profileData.full_name}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {profileData.email}
              </p>
              <div className="flex gap-2 mt-2">
                {/* Plan badge: prefer App-level userPlan (same cache as sidebar = always
                    consistent with sidebar) over profileData.plan which can be stale. */}
                {(() => {
                  const planForBadge = userPlan?.display_name ? userPlan : profileData.plan;
                  if (!planForBadge?.display_name) return null;
                  return (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      planForBadge.name === 'free'
                        ? isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                        : isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {planForBadge.display_name}
                    </span>
                  );
                })()}
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  profileData.status === 'active'
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                }`}>
                  {profileData.status || 'active'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation - COMPLETELY REMOVED from both Mobile and Desktop */}
      <div className="hidden">
        <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex overflow-x-auto">
            {tabs
              .filter(tab => tab.id !== 'dashboard' && tab.id !== 'membership')
              .map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors relative ${
                  activeTab === tab.id
                    ? isDarkMode
                      ? 'border-b-2 border-blue-500 text-blue-400'
                      : 'border-b-2 border-blue-600 text-blue-600'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-gray-300'
                      : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {tab.icon} {tab.label}
                {tab.id === 'messages' && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className={`${activeTab === 'games' ? '' : `rounded-lg shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}`}>
        <div className={`${activeTab === 'games' ? '' : 'p-6'}`}>
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              {/* Announcements Section - Moroccan Inspired Design */}
              {announcements && announcements.length > 0 ? (
                <div className="space-y-4">
                  <h4 className={`font-bold text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <span className="text-2xl">📢</span>
                    <span>{t('announcements')}</span>
                  </h4>
                  {announcements.map((announcement, index) => {
                    // Get notification type icon and colors
                    const getAnnouncementStyle = (type) => {
                      switch(type) {
                        case 'info':
                          return { 
                            icon: 'ℹ️', 
                            iconBg: 'moroccan-info',
                            borderClass: 'border-info',
                            textColor: isDarkMode ? 'text-blue-100' : 'text-blue-900'
                          };
                        case 'warning':
                          return { 
                            icon: '⚠️', 
                            iconBg: 'moroccan-warning',
                            borderClass: 'border-warning',
                            textColor: isDarkMode ? 'text-orange-100' : 'text-orange-900'
                          };
                        case 'success':
                          return { 
                            icon: '✅', 
                            iconBg: 'moroccan-success',
                            borderClass: 'border-success',
                            textColor: isDarkMode ? 'text-green-100' : 'text-green-900'
                          };
                        case 'update':
                          return { 
                            icon: '🔔', 
                            iconBg: 'moroccan-update',
                            borderClass: 'border-update',
                            textColor: isDarkMode ? 'text-purple-100' : 'text-purple-900'
                          };
                        case 'event':
                          return { 
                            icon: '📅', 
                            iconBg: 'moroccan-event',
                            borderClass: 'border-event',
                            textColor: isDarkMode ? 'text-red-100' : 'text-red-900'
                          };
                        default:
                          return { 
                            icon: '📢', 
                            iconBg: 'moroccan-announcement',
                            borderClass: 'border-announcement',
                            textColor: isDarkMode ? 'text-orange-100' : 'text-orange-900'
                          };
                      }
                    };
                    
                    const style = getAnnouncementStyle(announcement.notification_type || 'info');
                    
                    return (
                      <div
                        key={announcement.id}
                        className="moroccan-announcement-card announcement-card p-5"
                        style={{
                          background: announcement.bg_color || (isDarkMode ? '#1f2937' : '#ffffff')
                        }}
                      >
                        <div className="announcement-glow"></div>
                        <div className="moroccan-tiles absolute inset-0 opacity-50"></div>
                        
                        <div className="relative z-10 flex gap-4">
                          {/* Icon */}
                          <div className={`announcement-icon ${style.iconBg}`}>
                            <span>{style.icon}</span>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h5 
                              className={`font-bold text-lg mb-2 ${style.textColor}`}
                              style={{ color: announcement.text_color }}
                            >
                              {announcement.title}
                            </h5>
                            <p 
                              className={`text-sm leading-relaxed ${
                                isDarkMode ? 'text-gray-300' : 'text-gray-700'
                              }`}
                              style={{ color: announcement.text_color }}
                            >
                              {announcement.content}
                            </p>
                            
                            {/* Notification Type Badge */}
                            <div className="mt-3">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100/50'
                              } ${style.textColor}`}>
                                {(announcement.notification_type || 'info').toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`moroccan-announcement-card p-6 text-center ${
                  isDarkMode ? 'bg-gray-800/90' : 'bg-gradient-to-br from-blue-50 to-indigo-50'
                }`}>
                  <div className="moroccan-pattern absolute inset-0 opacity-30"></div>
                  <div className="relative z-10">
                    <div className="inline-block mb-3">
                      <div className={`announcement-icon moroccan-info`}>
                        <span>📢</span>
                      </div>
                    </div>
                    <h4 className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      {t('no_announcements')}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                      {t('check_back_later')}
                    </p>
                  </div>
                </div>
              )}

              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('welcome_back_name').replace('{name}', profileData.full_name)}
              </h3>

              {/* Member Since / Current Plan / Account Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('member_since')}</p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {new Date(profileData.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('current_plan')}</p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {profileData.plan?.display_name || 'Free'}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('account_status')}</p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {profileData.status === 'active' ? `✅ ${t('status_active')}` : `❌ ${t('status_inactive')}`}
                  </p>
                </div>
              </div>

            </div>
          )}

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {t('edit_profile')}
                </h3>
                {!editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    ✏️ {t('edit_profile')}
                  </button>
                )}
              </div>

              {/* Avatar Selection - Grouped by Gender (hidden when no avatars) */}
              {avatars.length > 0 && <div>
                <h4 className={`text-base lg:text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {t('choose_avatar')}
                </h4>
                
                {/* Render avatar button helper */}
                {(() => {
                  const baseUrl = API.replace(/\/api$/, '');
                  const renderAvatarButton = (avatar) => {
                    const raw = avatar.image_url;
                    const imageUrl = raw?.startsWith('/') ? `${baseUrl}${raw}` : raw?.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
                    return (
                      <button
                        key={avatar.id}
                        onClick={() => handleAvatarSelect(avatar.id)}
                        className={`p-1 lg:p-2 rounded-lg border-2 transition-all hover:scale-110 flex items-center justify-center overflow-hidden ${
                          profileData.avatar?.id === avatar.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-400'
                            : isDarkMode 
                              ? 'border-gray-600 hover:border-gray-500'
                              : 'border-gray-300 hover:border-gray-400'
                        }`}
                        title={avatar.name}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={avatar.name}
                            width="80" height="80"
                            loading="eager"
                            decoding="async"
                            className="w-16 h-16 lg:w-20 lg:h-20 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <span className={`text-2xl lg:text-4xl ${imageUrl ? 'hidden' : ''}`}>
                          👤
                        </span>
                      </button>
                    );
                  };

                  // Use dynamic genders from API or fallback to defaults (Female & Male only)
                  const gendersToUse = avatarGenders.length > 0 
                    ? avatarGenders.filter(g => g.is_active)
                    : [
                        { id: 'female', name: 'Female', color: '#ec4899' },
                        { id: 'male', name: 'Male', color: '#3b82f6' }
                      ];

                  // Get all known gender IDs
                  const knownGenderIds = gendersToUse.map(g => g.id);
                  
                  // Group avatars by gender
                  const groupedAvatars = {};
                  gendersToUse.forEach(gender => {
                    groupedAvatars[gender.id] = avatars.filter(a => a.gender === gender.id);
                  });
                  
                  // Also collect avatars with unknown/unset gender into the last category
                  const uncategorizedAvatars = avatars.filter(a => !a.gender || !knownGenderIds.includes(a.gender));
                  if (uncategorizedAvatars.length > 0 && gendersToUse.length > 0) {
                    const lastGender = gendersToUse[gendersToUse.length - 1];
                    groupedAvatars[lastGender.id] = [...(groupedAvatars[lastGender.id] || []), ...uncategorizedAvatars];
                  }

                  return (
                    <div className="space-y-4">
                      {gendersToUse.map(gender => {
                        const genderAvatars = groupedAvatars[gender.id] || [];
                        if (genderAvatars.length === 0) return null;
                        
                        return (
                          <div key={gender.id}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-medium" style={{ color: gender.color }}>{gender.name}</span>
                              <div className="flex-1 h-px" style={{ backgroundColor: `${gender.color}40` }}></div>
                            </div>
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                              {genderAvatars.map(renderAvatarButton)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>}

              {/* Profile Edit Form */}
              {editMode ? (
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('full_name')}
                    </label>
                    <input
                      type="text"
                      value={editData.full_name}
                      onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('email')}
                    </label>
                    <input
                      type="email"
                      value={editData.email}
                      disabled
                      className={`w-full px-4 py-2 border rounded-lg cursor-not-allowed opacity-60 ${
                        isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500'
                      }`}
                    />
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {featureFlags.notification_settings?.email_security_message || t('email_no_change')}
                    </p>
                  </div>

                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {t('change_password')}
                    </h5>
                    <div className="space-y-3">
                      <input
                        type="password"
                        placeholder={t('current_password')}
                        value={editData.current_password}
                        onChange={(e) => setEditData({...editData, current_password: e.target.value})}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                        }`}
                      />
                      <input
                        type="password"
                        placeholder={t('new_password')}
                        value={editData.new_password}
                        onChange={(e) => setEditData({...editData, new_password: e.target.value})}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      {t('save_changes')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditMode(false);
                        setEditData({ full_name: profileData.full_name, email: profileData.email, current_password: '', new_password: '' });
                      }}
                      className={`px-4 py-2 rounded-lg ${
                        isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                      }`}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('full_name')}</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {profileData.full_name}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('email')}</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {profileData.email}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('language')}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <select
                        value={selectedLang}
                        onChange={(e) => setSelectedLang(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm font-semibold ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'
                        }`}
                      >
                        {LANGUAGES.map(l => (
                          <option key={l.code} value={l.code}>{l.name}</option>
                        ))}
                      </select>
                      {selectedLang !== lang && (
                        <button
                          onClick={() => setLang(selectedLang)}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors"
                        >
                          {t('save')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* LEARNING GOALS SECTION */}
              {/* NOTIFICATION SETTINGS SECTION */}
              {/* Wait for feature flags to load before showing — prevents admin-hidden items from flashing visible */}
              {featureFlagsLoaded && featureFlags.notification_settings?.enabled && (
                <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'} mt-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      🔔 {t('notification_settings')}
                    </h3>
                    {featureFlags.notification_settings?.premium && !profileData.hasPaidPlan && (
                      <span className="px-3 py-1 bg-orange-500 text-white text-xs rounded-full font-semibold">
                        PRO
                      </span>
                    )}
                  </div>

                  {featureFlags.notification_settings?.premium && !profileData.hasPaidPlan ? (
                    <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-orange-900/20 border border-orange-700' : 'bg-orange-50 border border-orange-300'}`}>
                      <p className={`mb-3 ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                        🔒 {t('notification_pro_message')}
                      </p>
                      <button
                        onClick={() => setActiveTab('membership')}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
                      >
                        Upgrade to Pro
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {featureFlags.notification_settings?.notif_email_visible !== false && (
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userSettings.email_notifications}
                          onChange={(e) => setUserSettings({...userSettings, email_notifications: e.target.checked})}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          📧 {t('email_notifications')}
                        </span>
                      </label>
                      )}

                      {featureFlags.notification_settings?.notif_learning_reminders_visible !== false && (
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userSettings.learning_reminders}
                          onChange={(e) => setUserSettings({...userSettings, learning_reminders: e.target.checked})}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          ⏰ {t('daily_learning_reminders')}
                        </span>
                      </label>
                      )}

                      {featureFlags.notification_settings?.notif_achievement_visible !== false && (
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userSettings.achievement_notifications}
                          onChange={(e) => setUserSettings({...userSettings, achievement_notifications: e.target.checked})}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          🏆 {t('achievement_alerts')}
                        </span>
                      </label>
                      )}

                      {featureFlags.notification_settings?.notif_subscription_visible !== false && (
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userSettings.subscription_reminders}
                          onChange={(e) => setUserSettings({...userSettings, subscription_reminders: e.target.checked})}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          💳 {t('subscription_reminders')}
                        </span>
                      </label>
                      )}

                      {featureFlags.notification_settings?.notif_holiday_visible !== false && (
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userSettings.holiday_reminders || false}
                          onChange={(e) => setUserSettings({...userSettings, holiday_reminders: e.target.checked})}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          🎉 {t('holiday_reminders')}
                        </span>
                      </label>
                      )}

                      <button
                        onClick={async () => {
                          const result = await saveUserSettings({
                            email_notifications: userSettings.email_notifications,
                            learning_reminders: userSettings.learning_reminders,
                            achievement_notifications: userSettings.achievement_notifications,
                            subscription_reminders: userSettings.subscription_reminders,
                            holiday_reminders: userSettings.holiday_reminders
                          });
                          showNotification(result.message || 'Notification settings saved successfully!', 'success');
                        }}
                        disabled={savingSettings}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 mt-4"
                      >
                        {savingSettings ? t('saving') : '💾 ' + t('save_changes')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* WEATHER SETTINGS SECTION */}
              <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'} mt-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    🌤️ {t('weather_settings')}
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* Enable Weather Toggle */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🌡️</span>
                      <div>
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {t('show_weather')}
                        </span>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t('display_weather_header')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setUserSettings(prev => ({ ...prev, weather_enabled: !prev.weather_enabled }))}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        userSettings.weather_enabled !== false ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        userSettings.weather_enabled !== false ? 'translate-x-6' : 'translate-x-0.5'
                      }`}></div>
                    </button>
                  </label>

                  {/* City Selection */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      📍 {t('your_city')}
                    </label>
                    <select
                      value={userSettings.weather_city || 'Casablanca'}
                      onChange={(e) => setUserSettings(prev => ({ ...prev, weather_city: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="Agadir">Agadir</option>
                      <option value="Al Hoceima">Al Hoceima</option>
                      <option value="Asilah">Asilah</option>
                      <option value="Azrou">Azrou</option>
                      <option value="Beni Ansar">Beni Ansar</option>
                      <option value="Beni Mellal">Beni Mellal</option>
                      <option value="Berkane">Berkane</option>
                      <option value="Berrechid">Berrechid</option>
                      <option value="Casablanca">Casablanca</option>
                      <option value="Chefchaouen">Chefchaouen</option>
                      <option value="Dakhla">Dakhla</option>
                      <option value="El Jadida">El Jadida</option>
                      <option value="Errachidia">Errachidia</option>
                      <option value="Essaouira">Essaouira</option>
                      <option value="Fes">Fes</option>
                      <option value="Fnideq">Fnideq</option>
                      <option value="Guelmim">Guelmim</option>
                      <option value="Ifrane">Ifrane</option>
                      <option value="Imlil">Imlil</option>
                      <option value="Inezgane">Inezgane</option>
                      <option value="Jerada">Jerada</option>
                      <option value="Kenitra">Kenitra</option>
                      <option value="Khemisset">Khemisset</option>
                      <option value="Khouribga">Khouribga</option>
                      <option value="Laayoune">Laayoune</option>
                      <option value="Larache">Larache</option>
                      <option value="Marrakech">Marrakech</option>
                      <option value="Mdiq">Mdiq</option>
                      <option value="Meknes">Meknes</option>
                      <option value="Merzouga">Merzouga</option>
                      <option value="Midelt">Midelt</option>
                      <option value="Mohammedia">Mohammedia</option>
                      <option value="Nador">Nador</option>
                      <option value="Ouarzazate">Ouarzazate</option>
                      <option value="Oujda">Oujda</option>
                      <option value="Rabat">Rabat</option>
                      <option value="Safi">Safi</option>
                      <option value="Sale">Sale</option>
                      <option value="Settat">Settat</option>
                      <option value="Sidi Kacem">Sidi Kacem</option>
                      <option value="Sidi Slimane">Sidi Slimane</option>
                      <option value="Tafraout">Tafraout</option>
                      <option value="Tan-Tan">Tan-Tan</option>
                      <option value="Tangier">Tangier</option>
                      <option value="Taourirt">Taourirt</option>
                      <option value="Taroudant">Taroudant</option>
                      <option value="Taza">Taza</option>
                      <option value="Temara">Temara</option>
                      <option value="Tetouan">Tetouan</option>
                      <option value="Tinghir">Tinghir</option>
                      <option value="Tiznit">Tiznit</option>
                      <option value="Zagora">Zagora</option>
                    </select>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={async () => {
                      try {
                        const formData = new FormData();
                        formData.append('weather_enabled', userSettings.weather_enabled !== false);
                        formData.append('weather_city', userSettings.weather_city || 'Casablanca');
                        
                        const response = await fetch(`${API}/user/weather-settings`, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${userToken}` },
                          body: formData
                        });
                        
                        if (response.ok) {
                          showNotification('Weather settings saved!', 'success');
                          // Dispatch event to refresh weather in header immediately
                          window.dispatchEvent(new CustomEvent('weatherSettingsChanged'));
                        } else {
                          showNotification('Failed to save weather settings', 'error');
                        }
                      } catch (error) {
                        showNotification('Error saving settings', 'error');
                      }
                    }}
                    disabled={savingSettings}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingSettings ? t('saving') : '💾 ' + t('save_weather_settings')}
                  </button>
                </div>
              </div>

              {/* Devices & Activity Section */}
              <DevicesCard
                API={API}
                userToken={userToken}
                isDarkMode={isDarkMode}
                showNotification={showNotification}
              />

              {/* Appearance Section */}
              <div className={`p-6 rounded-lg shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {t('profile_appearance')}
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{isDarkMode ? '🌙' : '☀️'}</span>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('profile_dark_mode_label')}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{isDarkMode ? t('profile_currently_dark') : t('profile_currently_light')}</p>
                    </div>
                  </div>
                  <button
                    onClick={onToggleDarkMode}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDarkMode ? 'bg-blue-600' : 'bg-gray-300'}`}
                    role="switch"
                    aria-checked={isDarkMode}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {/* Account Actions Section */}
              <div className={`p-6 rounded-lg shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 font-semibold transition-colors shadow-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>

              {/* Logout Confirmation Modal */}
              {showLogoutModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={() => setShowLogoutModal(false)}>
                  <div
                    className={`max-w-sm w-full rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mb-4">
                        <svg className="w-7 h-7 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Log out of DarijaPro?</h3>
                      <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>You'll need to sign in again to access your account.</p>
                      <div className="flex gap-3 w-full">
                        <button
                          onClick={() => setShowLogoutModal(false)}
                          className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => { setShowLogoutModal(false); onLogout(); }}
                          className="flex-1 px-4 py-2.5 rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors shadow-sm"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Membership Tab */}
          {activeTab === "membership" && (
            <div className="space-y-6">
              {/* Current Plan Card */}
              <div className={`rounded-2xl overflow-hidden ${
                profileData.plan?.name === 'free'
                  ? isDarkMode ? 'bg-gray-800 border border-gray-700/60' : 'bg-white border border-gray-200/80'
                  : isDarkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200'
              } shadow-sm`}>
                <div className="p-5 lg:p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Current Plan</p>
                      <h4 className={`text-2xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {profileData.plan?.display_name || 'Free Plan'}
                      </h4>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className={`text-lg font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {profileData.plan?.name === 'free' ? 'Free Forever' : `$${profileData.plan?.price}`}
                        </span>
                        {profileData.plan?.name !== 'free' && (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>/{profileData.plan?.billing_period}</span>
                        )}
                      </div>
                      {profileData.plan?.name !== 'free' && (
                        <p className={`text-xs mt-1.5 font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          ✓ Includes all Free plan features
                        </p>
                      )}
                      {/* MEMBERSHIP FIX: Show start and end dates for paid plans */}
                      {profileData.plan?.name !== 'free' && (profileData.subscription_start_date || profileData.subscription?.start_date) && (
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Started: {new Date(profileData.subscription_start_date || profileData.subscription?.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      )}
                      {profileData.plan?.name !== 'free' && (profileData.subscription_end_date || profileData.subscription?.end_date) && (
                        <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Expires: {new Date(profileData.subscription_end_date || profileData.subscription?.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                    {profileData.subscription?.will_cancel ? (
                      <div className="text-right">
                        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-orange-500 text-white">
                          Cancelled
                        </span>
                        <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          on {profileData.subscription.cancelled_at ? new Date(profileData.subscription.cancelled_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    ) : (
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                        profileData.subscription?.status === 'active'
                          ? isDarkMode ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {profileData.subscription?.status || 'Active'}
                      </span>
                    )}
                  </div>

                  {profileData.plan?.description && (
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {profileData.plan.description}
                    </p>
                  )}

                  {/* Cancel Subscription Button */}
                  {profileData.plan?.name !== 'free' && profileData.subscription?.status === 'active' && !profileData.subscription?.will_cancel && (
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${isDarkMode ? 'bg-red-900/30 text-red-400 border border-red-500/30 hover:bg-red-900/50' : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'}`}
                    >
                      Cancel Subscription
                    </button>
                  )}

                  {/* Cancellation Notice */}
                  {profileData.subscription?.will_cancel && (
                    <div className={`p-3.5 rounded-xl ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700/40' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <p className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                        Subscription Scheduled for Cancellation
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-200/70' : 'text-yellow-700'}`}>
                        Your subscription will end on {profileData.subscription?.end_date ? new Date(profileData.subscription.end_date).toLocaleDateString() : 'the end of your billing period'}. You can continue using all features until then.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Available Plans - Professional Design */}
              {membershipPlans.length > 0 && (
                <div>
                  <div className="text-center mb-5">
                    <h4 className={`text-lg lg:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {profileData.plan?.name === 'free' ? 'Choose Your Plan' : 'Available Plans'}
                    </h4>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Unlock the full Darija learning experience
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 md:flex md:gap-5">
                    {membershipPlans.map(plan => {
                      const isActive = plan.name === profileData.plan?.name;
                      const isFree = plan.name === 'free';
                      const isPopular = !isFree && plan.is_popular;
                      const hasActivePaidPlan = profileData.subscription?.status === 'active' && !profileData.subscription?.will_cancel && profileData.plan?.name !== 'free';
                      const isUpgrade = hasActivePaidPlan && !isActive && !isFree && plan.price > (profileData.plan?.price || 0);
                      const isDowngrade = hasActivePaidPlan && !isActive && !isFree && plan.price <= (profileData.plan?.price || 0);
                      return (
                        <div
                          key={plan.id}
                          data-plan-id={plan.id}
                          className={`relative rounded-2xl overflow-hidden transition-all duration-300 flex flex-col md:flex-1 md:min-w-0 ${
                            isActive
                              ? isDarkMode
                                ? 'ring-2 ring-emerald-400/70 bg-gray-800 border border-emerald-500/20'
                                : 'ring-2 ring-emerald-500 bg-white border border-emerald-200'
                              : isPopular
                              ? isDarkMode
                                ? 'bg-gradient-to-b from-purple-900/40 via-gray-800 to-gray-800 border-2 border-purple-500/50 shadow-xl shadow-purple-500/10 hover:shadow-2xl hover:shadow-purple-500/20 hover:border-purple-400/70'
                                : 'bg-gradient-to-b from-purple-50 via-white to-white border-2 border-purple-300 shadow-xl shadow-purple-200/40 hover:shadow-2xl hover:shadow-purple-300/50 hover:border-purple-400'
                              : isFree
                              ? isDarkMode
                                ? 'bg-gray-800 border border-gray-700/60 hover:border-gray-600'
                                : 'bg-white border border-gray-200/80 hover:border-gray-300'
                              : isDarkMode
                                ? 'bg-gray-800 border border-gray-700/60 hover:border-blue-500/40'
                                : 'bg-white border border-gray-200/80 hover:border-blue-300'
                          } shadow-sm hover:shadow-lg`}
                        >
                          {/* Popular ribbon */}
                          {isPopular && (
                            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-center py-2 text-[11px] font-bold tracking-widest uppercase">
                              Most Popular
                            </div>
                          )}

                          {/* Active ribbon */}
                          {isActive && (
                            <div className={`text-center py-2 text-[11px] font-bold tracking-widest uppercase ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
                              Current Plan
                            </div>
                          )}

                          <div className="p-5 lg:p-6 flex flex-col flex-1">
                            {/* Icon + Plan name */}
                            <div className="mb-4">
                              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3 ${
                                isPopular
                                  ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                                  : isFree
                                    ? isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                                    : isDarkMode ? 'bg-blue-900/50' : 'bg-blue-50'
                              }`}>
                                {isPopular ? (
                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                                ) : isFree ? (
                                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                ) : (
                                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                )}
                              </div>
                              <h5 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {plan.display_name}
                              </h5>
                              {plan.description && (
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {plan.description}
                                </p>
                              )}
                            </div>

                            {/* Price */}
                            <div className="mb-5">
                              <div className="flex items-baseline gap-1">
                                <span className={`text-3xl lg:text-4xl font-extrabold ${
                                  isPopular
                                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent'
                                    : isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {isFree ? 'Free' : `$${plan.price}`}
                                </span>
                                {!isFree && (
                                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    /{plan.billing_period}
                                  </span>
                                )}
                              </div>
                              {isFree && (
                                <p className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>No credit card required</p>
                              )}
                            </div>

                            {/* Divider */}
                            <div className={`border-t mb-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />

                            {/* Features */}
                            <div className="space-y-2.5 mb-6 flex-1">
                              {plan.display_features && plan.display_features.length > 0 ? (
                                // Render from unified display_features (admin-ordered)
                                plan.display_features.filter(f => f.enabled).map((feat, idx) => (
                                  <div key={idx} className="flex items-start gap-2.5">
                                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                                      isPopular
                                        ? isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'
                                        : isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-50'
                                    }`}>
                                      <svg className={`w-3 h-3 ${isPopular ? isDarkMode ? 'text-purple-400' : 'text-purple-600' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                    </div>
                                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feat.label}</span>
                                  </div>
                                ))
                              ) : (
                                // Fallback: render from features object (backwards compat)
                                <>
                                  {plan.features?.translations_per_day === -1 ? (
                                    <div className="flex items-start gap-2.5">
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isPopular ? isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100' : isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-50'}`}>
                                        <svg className={`w-3 h-3 ${isPopular ? isDarkMode ? 'text-purple-400' : 'text-purple-600' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                      </div>
                                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Unlimited translations</span>
                                    </div>
                                  ) : plan.features?.translations_per_day > 0 && (
                                    <div className="flex items-start gap-2.5">
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-50'}`}>
                                        <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                      </div>
                                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{plan.features.translations_per_day} translations/day</span>
                                    </div>
                                  )}
                                  {[
                                    { key: 'audio_enabled', label: 'Audio pronunciation' },
                                    { key: 'voice_input_enabled', label: 'Voice input' },
                                    { key: 'favorites_enabled', label: 'Save favorites' },
                                    { key: 'categories_enabled', label: 'Learning categories' },
                                    { key: 'daily_words_enabled', label: 'Explore (Listen & Guess)' },
                                    { key: 'progress_tracking_enabled', label: 'Word Bank' },
                                    { key: 'games_access', label: 'All games access' },
                                    { key: 'travel_tools_access', label: 'Travel Tools dictionary' },
                                    { key: 'conversation_challenge_access', label: 'Conversation challenge' },
                                    { key: 'request_word_enabled', label: 'Request new words' },
                                  ].filter(f => plan.features?.[f.key]).map((feat, idx) => (
                                    <div key={idx} className="flex items-start gap-2.5">
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isPopular ? isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100' : isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-50'}`}>
                                        <svg className={`w-3 h-3 ${isPopular ? isDarkMode ? 'text-purple-400' : 'text-purple-600' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                      </div>
                                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feat.label}</span>
                                    </div>
                                  ))}
                                  {plan.custom_features?.map((feat, idx) => feat && (
                                    <div key={`custom-${idx}`} className="flex items-start gap-2.5">
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isPopular ? isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100' : isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-50'}`}>
                                        <svg className={`w-3 h-3 ${isPopular ? isDarkMode ? 'text-purple-400' : 'text-purple-600' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                      </div>
                                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feat}</span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>

                            {/* Button — three states:
                                A) THIS plan + scheduled to cancel → Resume Subscription
                                B) THIS plan + not cancelled → Current Plan badge
                                C) DIFFERENT paid plan active → blocked, contact-support message
                                D) Otherwise → Get Started / Free Forever                                         */}
                            {(() => {
                              const onCurrentPlanCancelling = isActive && profileData.subscription?.will_cancel;
                              // Lock OTHER paid plans whenever user has ANY active subscription
                              // (including cancelled-but-still-active) so they can't double-bill.
                              // Plan switching is admin-handled via support email.
                              const hasAnyActiveSub = profileData.subscription?.status === 'active' && profileData.plan?.name !== 'free';
                              const onDifferentActivePlan = !isActive && hasAnyActiveSub && !isFree;

                              if (onCurrentPlanCancelling) {
                                const endDateText = profileData.subscription?.end_date
                                  ? new Date(profileData.subscription.end_date).toLocaleDateString()
                                  : '';
                                return (
                                  <div className="space-y-2">
                                    <div className={`text-xs text-center ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                      ⚠️ Cancels on {endDateText}
                                    </div>
                                    <button
                                      onClick={async () => {
                                        try {
                                          const response = await fetch(`${API}/user/subscription/resume`, {
                                            method: 'POST',
                                            headers: { 'Authorization': `Bearer ${userToken}` }
                                          });
                                          if (response.ok) {
                                            const result = await response.json();
                                            showNotification(result.message || 'Subscription resumed', 'success');
                                            loadProfile();
                                          } else {
                                            const error = await response.json();
                                            showNotification(error.detail || 'Failed to resume subscription', 'error');
                                          }
                                        } catch (e) {
                                          showNotification('Failed to resume subscription', 'error');
                                        }
                                      }}
                                      className="w-full py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-lg transition-all"
                                    >
                                      🔄 Resume Subscription
                                    </button>
                                  </div>
                                );
                              }

                              if (onDifferentActivePlan) {
                                return (
                                  <div className="space-y-2">
                                    <button
                                      disabled
                                      className={`w-full py-3.5 rounded-xl font-bold text-sm cursor-not-allowed ${isDarkMode ? 'bg-gray-700/50 text-gray-500' : 'bg-gray-100 text-gray-400'}`}
                                    >
                                      🔒 Locked
                                    </button>
                                    <p className={`text-xs text-center leading-snug ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                      Currently on <strong>{profileData.plan?.display_name || 'Pro'}</strong>.<br />
                                      To switch, email <a href="mailto:support@DarijaPro.com" className="text-blue-500 hover:underline">support@DarijaPro.com</a>
                                    </p>
                                  </div>
                                );
                              }

                              // Default: existing button (Free / Current / Get Started)
                              return (
                                <button
                                  className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 ${
                                    isActive
                                      ? isDarkMode
                                        ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-500/30 cursor-default'
                                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                                      : isFree
                                      ? isDarkMode
                                        ? 'bg-gray-700/50 text-gray-500 cursor-default'
                                        : 'bg-gray-100 text-gray-400 cursor-default'
                                      : loadingPurchase
                                      ? 'bg-gray-400 cursor-not-allowed text-white'
                                      : isPopular
                                      ? 'bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white hover:from-purple-700 hover:via-pink-600 hover:to-orange-600 shadow-lg shadow-purple-500/25 hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                                  }`}
                                  onClick={() => !isFree && !isActive && !loadingPurchase && handlePurchasePlan(plan.id, plan.display_name)}
                                  disabled={loadingPurchase || isActive || isFree}
                                >
                                  {loadingPurchase && !isActive && !isFree ? '⏳ Processing...' :
                                   isActive ? '✓ Your Current Plan' :
                                   isFree ? 'Free Forever' :
                                   '🚀 Get Started'}
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Payment Disclaimer */}
              {paymentDisclaimer.enabled && paymentDisclaimer.text && (
                <div className={`flex items-start gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-blue-50/60 border border-blue-100'}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
                    <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className={`text-xs font-bold mb-0.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      Secure Payments
                    </h4>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {paymentDisclaimer.text}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab - MODERN GRADIENT DESIGN */}
          {activeTab === "orders" && (() => {
            const availableYears = [...new Set(orders.map(o => new Date(o.created_at).getFullYear()))].sort((a, b) => b - a);
            const filteredOrders = ordersYearFilter === 'all' ? orders : orders.filter(o => new Date(o.created_at).getFullYear() === parseInt(ordersYearFilter));
            const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
            const safePage = Math.min(ordersPage, totalPages);
            const paginatedOrders = filteredOrders.slice((safePage - 1) * ORDERS_PER_PAGE, safePage * ORDERS_PER_PAGE);
            return (
            <div className="space-y-6">
              {/* Membership card removed — managed in Membership tab */}

              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}>
                  📦 {t('customer_order_history')}
                </h3>
              </div>

              {/* Empty state — only shown to free users with no orders.
                  Paid users with no orders is an edge case (recently downgraded);
                  they fall through silently like before. */}
              {orders.length === 0 && !profileData.hasPaidPlan && (
                <div className={`rounded-2xl p-10 text-center ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700'
                    : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'
                }`}>
                  <div className="text-6xl mb-4 opacity-50">📦</div>
                  <h4 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    No orders yet
                  </h4>
                  <p className={`text-sm mb-6 max-w-md mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    When you upgrade to Pro, your purchase history will appear here.
                  </p>
                  <button
                    onClick={() => setActiveTab('membership')}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-sm hover:opacity-90 transition-opacity shadow-lg"
                  >
                    View Plans
                  </button>
                </div>
              )}

              {/* Detailed Order History Table */}
              {orders.length > 0 && (
                <div className={`rounded-2xl shadow-2xl overflow-hidden ${
                  isDarkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-white to-gray-50'
                }`}>
                  {/* Table Header */}
                  <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50'}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        📋 {t('detailed_order_history')}
                      </h4>
                      {availableYears.length > 0 && (
                        <select
                          value={ordersYearFilter}
                          onChange={(e) => { setOrdersYearFilter(e.target.value); setOrdersPage(1); }}
                          className={`px-3 py-1.5 rounded-lg text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                        >
                          <option value="all">All years</option>
                          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Mobile cards (< md) — stacked layout, all order info visible */}
                  <div className={`md:hidden divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {paginatedOrders.map((order, index) => (
                      <div key={`m-${order.id}`} className="p-4">
                        <div className="flex justify-between items-start mb-3 gap-2">
                          <div className="min-w-0 flex-1">
                            <div className={`font-mono font-bold text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              {order.order_number || `ORD-${String(index + 1).padStart(6, '0')}`}
                            </div>
                            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                            order.status === 'completed' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                            : order.status === 'pending' ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                            : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          }`}>
                            {order.status === 'completed' && '✓ '}
                            {order.status === 'pending' && '⏳ '}
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className={`text-[11px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('plan')}</div>
                            <div className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{order.plan_display_name || order.plan_name || 'Plan'}</div>
                          </div>
                          <div>
                            <div className={`text-[11px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('amount')}</div>
                            <div className={`font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>${order.amount || '0.00'}</div>
                          </div>
                          <div>
                            <div className={`text-[11px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('expires')}</div>
                            <div className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}>
                              {order.expiration_date || order.end_date ? new Date(order.expiration_date || order.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                            </div>
                          </div>
                          <div>
                            <div className={`text-[11px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('method')}</div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              (order.acquisition_method === 'admin_granted' || order.payment_method === 'admin' || order.payment_method === 'manual')
                                ? isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700'
                                : isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {(order.acquisition_method === 'admin_granted' || order.payment_method === 'admin' || order.payment_method === 'manual') ? `👤 ${t('admin_granted')}` : `💳 ${t('purchase')}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop table (>= md) — unchanged */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-gray-50'}`}>
                        <tr>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('order_number')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('customer')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('plan')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('amount')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('status')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('created')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('expires')}
                          </th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('method')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {/* Real orders */}
                        {paginatedOrders.map((order, index) => (
                          <tr 
                            key={order.id}
                            className={`transition-colors ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-blue-50/50'}`}
                          >
                            <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              <span className={`font-mono font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                {order.order_number || `ORD-${String(index + 1).padStart(6, '0')}`}
                              </span>
                            </td>
                            <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              <div>
                                <p className="font-semibold">{order.user_name || profileData.full_name || 'Customer'}</p>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{order.user_email || profileData.email}</p>
                              </div>
                            </td>
                            <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              <span className="font-semibold">{order.plan_display_name || order.plan_name || 'Plan'}</span>
                            </td>
                            <td className={`px-6 py-4 text-sm font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              ${order.amount || '0.00'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                                order.status === 'completed' 
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                                  : order.status === 'pending'
                                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              }`}>
                                {order.status === 'completed' && '✓'}
                                {order.status === 'pending' && '⏳'}
                                {order.status.toUpperCase()}
                              </span>
                            </td>
                            <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {new Date(order.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </td>
                            <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {order.expiration_date || order.end_date ? new Date(order.expiration_date || order.end_date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              }) : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                                (order.acquisition_method === 'admin_granted' || order.payment_method === 'admin' || order.payment_method === 'manual')
                                  ? isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700'
                                  : isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {(order.acquisition_method === 'admin_granted' || order.payment_method === 'admin' || order.payment_method === 'manual') ? `👤 ${t('admin_granted')}` : `💳 ${t('purchase')}`}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination — Previous / Page X of Y / Next */}
                  {totalPages > 1 && (
                    <div className={`flex items-center justify-between px-4 sm:px-6 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                        disabled={safePage <= 1}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${safePage <= 1 ? 'opacity-40 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      >← Previous</button>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Page {safePage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setOrdersPage(p => Math.min(totalPages, p + 1))}
                        disabled={safePage >= totalPages}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${safePage >= totalPages ? 'opacity-40 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      >Next →</button>
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {/* Messages Tab */}
          {activeTab === "messages" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {t('messages')}
                </h3>
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  ✉️ {t('new_message')}
                </button>
              </div>
              
              {editMode ? (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const formData = new FormData();
                    formData.append('subject', editData.subject || '');
                    formData.append('content', editData.message || '');
                    
                    const response = await fetch(`${API}/messages/send`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${userToken}`
                      },
                      body: formData
                    });
                    
                    if (response.ok) {
                      showNotification('Message sent to admin!');
                      setEditMode(false);
                      setEditData({...editData, subject: '', message: ''});
                      loadMessages();
                    } else {
                      const errorData = await response.json();
                      showNotification(errorData.detail || 'Failed to send message', 'error');
                    }
                  } catch (error) {
                    console.error('Error:', error);
                    showNotification('Failed to send message', 'error');
                  }
                }} className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="space-y-4">
                    <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                      <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        To: <span className="text-blue-600">Admin</span>
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        From: {profileData.full_name}
                      </p>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Subject
                      </label>
                      <input
                        type="text"
                        value={editData.subject || ''}
                        onChange={(e) => setEditData({...editData, subject: e.target.value})}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="What is this about?"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Message
                      </label>
                      <textarea
                        value={editData.message || ''}
                        onChange={(e) => setEditData({...editData, message: e.target.value})}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                        }`}
                        rows="6"
                        placeholder="Type your message here..."
                        required
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                      >
                        📤 Send Message
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditMode(false)}
                        className={`px-4 py-2 rounded-lg ${
                          isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              ) : messages.length > 0 ? (
                <div className="space-y-4">
                  {messages.map(message => (
                    <div 
                      key={message.id}
                      className={`p-4 rounded-lg border ${
                        message.from_user_id === 'admin'
                          ? isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-300 bg-blue-50'
                          : isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {message.from_user_id === 'admin' ? '👨‍💼 Admin' : '📤 You'} → {message.to_user_id === 'admin' ? '👨‍💼 Admin' : '📥 You'}
                          </p>
                          <p className={`text-lg font-semibold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {message.subject}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {new Date(message.created_at).toLocaleString()}
                          </p>
                          <button
                            onClick={() => {
                              setMessageToDelete(message);
                              setShowDeleteModal(true);
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode 
                                ? 'hover:bg-red-900/20 text-red-400' 
                                : 'hover:bg-red-50 text-red-600'
                            }`}
                            title="Delete message"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {message.content}
                      </p>
                      {message.replies && message.replies.length > 0 && (
                        <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Replies:
                          </p>
                          {message.replies.map((reply, idx) => (
                            <div key={idx} className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                              <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {reply.from === 'admin' ? '👨‍💼 Admin' : 'You'}:
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {reply.content}
                              </p>
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {reply.created_at && new Date(reply.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply Section */}
                      {replyingTo === message.id ? (
                        <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                          <textarea
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg mb-2 ${
                              isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'
                            }`}
                            rows="3"
                            placeholder="Type your reply..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleReplySubmit(message.id)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                            >
                              💬 Send Reply
                            </button>
                            <button
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyContent('');
                              }}
                              className={`px-4 py-2 rounded-lg text-sm ${
                                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                              }`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReplyingTo(message.id)}
                          className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isDarkMode 
                              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          💬 Reply
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="text-lg mb-2">💬 No messages yet</p>
                  <p className="text-sm">Click "New Message" to contact admin</p>
                </div>
              )}
            </div>
          )}

          {/* Dark mode override for WYSIWYG inline color styles */}
          {isDarkMode && (
            <style>{`
              .static-page-content span[style*="color"],
              .static-page-content font[color],
              .static-page-content p[style*="color"],
              .static-page-content div[style*="color"],
              .static-page-content li[style*="color"],
              .static-page-content h1[style*="color"],
              .static-page-content h2[style*="color"],
              .static-page-content h3[style*="color"] {
                color: inherit !important;
              }
            `}</style>
          )}

          {/* Help Tab */}
          {activeTab === "help" && (
            <div className="space-y-4">
              {/* Header */}
              <div className={`p-6 rounded-2xl bg-gradient-to-r ${isDarkMode ? 'from-blue-900/40 to-purple-900/30 border border-blue-700/50' : 'from-blue-50 to-indigo-50 border border-blue-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'} shadow-lg`}>
                    ?
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {staticPages.help?.title || helpContent?.content?.help_title || 'Help & Support'}
                    </h3>
                    {helpContent?.content?.help_intro && (
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {helpContent.content.help_intro}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {(loadingStaticPage || loadingHelpContent) && !staticPages.help && !helpContent ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading...</p>
                </div>
              ) : staticPages.help ? (
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                  <div
                    className={`text-base leading-relaxed static-page-content ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(staticPages.help.content) }}
                  />
                </div>
              ) : helpContent?.content ? (
                <>
                  {/* Contact card */}
                  {helpContent.content.contact_title && (
                    <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'} shadow-sm`}>
                      <h4 className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {helpContent.content.contact_title}
                      </h4>
                      <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {helpContent.content.contact_desc || 'Contact our support team.'}
                      </p>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {helpContent.content.contact_email || 'Email: support@DarijaPro.com'}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Content not available</p>
                </div>
              )}

              {/* Documentation sections — each in its own card */}
              {helpContent?.content && [
                { key: 'contact', icon: '📧' },
                { key: 'about', icon: '🌍' },
                { key: 'profile', icon: '👤' },
                { key: 'membership', icon: '💎' },
                { key: 'upgrade', icon: '⬆️' },
                { key: 'orders', icon: '🧾' },
                { key: 'labels', icon: '🏷️' },
                { key: 'progress', icon: '📊' },
                { key: 'features', icon: '✨' },
                { key: 'howto', icon: '📖' },
                { key: 'delete', icon: '🗑️' },
              ].map(({ key, icon }) => (
                helpContent.content[`doc_${key}_title`] && (
                  <div key={key} className={`p-5 rounded-xl border ${
                    key === 'delete'
                      ? (isDarkMode ? 'bg-red-900/10 border-red-700/40' : 'bg-red-50/50 border-red-200')
                      : (isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')
                  } shadow-sm`}>
                    <h4 className={`font-semibold text-base mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {helpContent.content[`doc_${key}_title`]}
                    </h4>
                    <div
                      className={`text-sm static-page-content ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(helpContent.content[`doc_${key}_content`] || '') }}
                    />
                    {key === 'delete' && userToken && userDeletionRequest && (
                      <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-orange-900/20 border border-orange-700/40' : 'bg-orange-50 border border-orange-200'}`}>
                        <p className="text-sm text-orange-500 mb-2 animate-pulse font-semibold">
                          Your account is scheduled for deletion
                        </p>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/user/delete-request`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${userToken}` }
                              });
                              if (res.ok) {
                                setUserDeletionRequest(null);
                              }
                            } catch (err) {
                              console.error('Failed to cancel deletion:', err);
                            }
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-sm"
                        >
                          Cancel Deletion Request
                        </button>
                      </div>
                    )}
                  </div>
                )
              ))}

            </div>
          )}

          {/* About Tab */}
          {activeTab === "about" && (
            <div className="space-y-6">
              <div className={`p-8 rounded-2xl bg-gradient-to-br ${
                isDarkMode
                  ? 'from-blue-900/40 via-purple-900/30 to-pink-900/40 border border-blue-700/50'
                  : 'from-blue-50 via-purple-50 to-pink-50 border border-blue-200'
              } shadow-xl`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
                    isDarkMode ? 'bg-blue-600' : 'bg-blue-500'
                  } shadow-lg`}>
                    ℹ️
                  </div>
                  <div>
                    <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {staticPages.about?.title || helpContent?.content?.about_title || 'About Us'}
                    </h3>
                  </div>
                </div>

                {loadingStaticPage && !staticPages.about && !helpContent ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Loading...
                    </p>
                  </div>
                ) : staticPages.about ? (
                  <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                    <div
                      className={`text-base leading-relaxed static-page-content ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(staticPages.about.content) }}
                    />
                  </div>
                ) : helpContent?.content ? (
                  <div className="space-y-6">
                    {/* Mission */}
                    {helpContent.content.about_mission && (
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>🎯 Our Mission</h4>
                        <p className={`text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {helpContent.content.about_mission}
                        </p>
                      </div>
                    )}

                    {/* Story */}
                    {helpContent.content.about_story && (
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                        <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>📖 Our Story</h4>
                        <p className={`text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {helpContent.content.about_story}
                        </p>
                      </div>
                    )}

                    {/* Features */}
                    {helpContent.content.about_features && (
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                        <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>✨ What We Offer</h4>
                        <p className={`text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {helpContent.content.about_features}
                        </p>
                      </div>
                    )}

                    {/* Team */}
                    {helpContent.content.about_team && (
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                        <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>👥 Our Team</h4>
                        <p className={`text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {helpContent.content.about_team}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Content not available
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Terms of Use Tab */}
          {activeTab === "terms" && (
            <div className="space-y-6">
              <div className={`p-8 rounded-2xl bg-gradient-to-br ${
                isDarkMode 
                  ? 'from-indigo-900/40 via-blue-900/30 to-cyan-900/40 border border-indigo-700/50' 
                  : 'from-indigo-50 via-blue-50 to-cyan-50 border border-indigo-200'
              } shadow-xl`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
                    isDarkMode ? 'bg-indigo-600' : 'bg-indigo-500'
                  } shadow-lg`}>
                    📜
                  </div>
                  <div>
                    <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {staticPages.terms?.title || 'Terms of Use'}
                    </h3>
                  </div>
                </div>
                
                {loadingStaticPage && !staticPages.terms ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Loading...
                    </p>
                  </div>
                ) : staticPages.terms ? (
                  <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                    <div 
                      className={`text-base leading-relaxed static-page-content ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(staticPages.terms.content) }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Content not available
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Privacy Policy Tab */}
          {activeTab === "privacy" && (
            <div className="space-y-6">
              <div className={`p-8 rounded-2xl bg-gradient-to-br ${
                isDarkMode 
                  ? 'from-green-900/40 via-emerald-900/30 to-teal-900/40 border border-green-700/50' 
                  : 'from-green-50 via-emerald-50 to-teal-50 border border-green-200'
              } shadow-xl`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
                    isDarkMode ? 'bg-green-600' : 'bg-green-500'
                  } shadow-lg`}>
                    🔒
                  </div>
                  <div>
                    <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {staticPages.privacy?.title || 'Privacy Policy'}
                    </h3>
                  </div>
                </div>
                
                {loadingStaticPage && !staticPages.privacy ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Loading...
                    </p>
                  </div>
                ) : staticPages.privacy ? (
                  <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                    <div 
                      className={`text-base leading-relaxed static-page-content ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(staticPages.privacy.content) }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Content not available
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Delete Account Tab */}
          {activeTab === "delete" && (
            <div className="space-y-6">
              <h3 className={`text-xl font-bold text-red-600`}>
                ⚠️ Delete Account
              </h3>
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700">
                <p className="text-red-700 dark:text-red-300 font-semibold mb-2">
                  Warning: This action is permanent and cannot be undone!
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Your account will be scheduled for deletion after 30 days. You can cancel within this period.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Please tell us why you're leaving
                  </label>
                  <div className="space-y-2">
                    {['Not using it anymore', 'Found a better alternative', 'Too expensive', 'Missing features', 'Privacy concerns', 'Other'].map(reason => (
                      <label key={reason} className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <input type="radio" name="delete_reason" value={reason} />
                        {reason}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('additional_details')}
                  </label>
                  <textarea
                    rows="4"
                    placeholder={t('tell_us_more')}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <button className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold">
                  {t('request_account_deletion')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Method Selection Modal */}
      {showPaymentMethodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-sm w-full rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Choose Payment Method
              </h3>
              <p className={`text-sm mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Select how you'd like to pay for <strong>{showPaymentMethodModal.planName}</strong>
              </p>
              <div className="space-y-3">
                {paymentProviders.stripe_enabled && (
                  <button
                    onClick={() => startStripeCheckout(showPaymentMethodModal.planId)}
                    disabled={loadingPurchase}
                    className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      isDarkMode
                        ? 'border-gray-600 hover:border-blue-500 bg-gray-700 text-white'
                        : 'border-gray-200 hover:border-blue-500 bg-white text-gray-900'
                    }`}
                  >
                    <span className="text-2xl">💳</span>
                    <div className="text-left">
                      <div className="font-semibold">Pay with Card</div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visa, Mastercard, Apple Pay, Google Pay</div>
                    </div>
                  </button>
                )}
                {paymentProviders.paypal_enabled && (
                  <button
                    onClick={() => startPayPalCheckout(showPaymentMethodModal.planId)}
                    disabled={loadingPurchase}
                    className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      isDarkMode
                        ? 'border-gray-600 hover:border-yellow-500 bg-gray-700 text-white'
                        : 'border-gray-200 hover:border-yellow-500 bg-white text-gray-900'
                    }`}
                  >
                    <span className="text-2xl">🅿️</span>
                    <div className="text-left">
                      <div className="font-semibold">Pay with PayPal</div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>PayPal account or card via PayPal</div>
                    </div>
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowPaymentMethodModal(null)}
                className={`w-full mt-4 py-2 rounded-lg text-sm ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('cancel_subscription')}
              </h3>
              <div className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="mb-3">⚠️ {t('cancel_confirm_question')}</p>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <p className="text-sm mb-3 font-semibold">
                    ✓ {t('membership_continues_until')}
                  </p>
                  <p className="text-lg font-bold mb-3">
                    {profileData?.subscription_end_date ? new Date(profileData.subscription_end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : t('end_billing_period')}
                  </p>
                  <p className="text-sm mb-2">
                    • {t('keep_full_access')}
                  </p>
                  <p className="text-sm mb-2">
                    • {t('can_resubscribe')}
                  </p>
                  <p className="text-sm font-bold text-red-600 dark:text-red-400">
                    • {t('refund_contact_admin')}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/user/subscription/cancel`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${userToken}`
                        }
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        showNotification(result.message, 'success');
                        loadProfile();
                        setShowCancelModal(false);
                      } else {
                        const error = await response.json();
                        showNotification(error.detail || 'Failed to cancel subscription', 'error');
                      }
                    } catch (error) {
                      console.error('Error:', error);
                      showNotification('Failed to cancel subscription', 'error');
                    }
                  }}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
                >
                  {t('yes_cancel_membership')}
                </button>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  {t('keep_subscription')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Message Modal */}
      {showDeleteModal && messageToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('delete_message')}
              </h3>
              <div className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="mb-3">{t('delete_message_confirm')}</p>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                  <p className="font-semibold mb-1">{messageToDelete.subject}</p>
                  <p className="text-sm line-clamp-2">{messageToDelete.content}</p>
                </div>
                <p className="text-sm mt-3 text-red-500">
                  {t('action_cannot_undone')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/user/messages/${messageToDelete.id}`, {
                        method: 'DELETE',
                        headers: {
                          'Authorization': `Bearer ${userToken}`
                        }
                      });
                      
                      if (response.ok) {
                        showNotification('Message deleted', 'success');
                        loadMessages();
                        setShowDeleteModal(false);
                        setMessageToDelete(null);
                      } else {
                        const errorData = await response.json();
                        showNotification(errorData.detail || 'Failed to delete message', 'error');
                      }
                    } catch (error) {
                      console.error('Error:', error);
                      showNotification('Failed to delete message', 'error');
                    }
                  }}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
                >
                  {t('delete_message')}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMessageToDelete(null);
                  }}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      )}

          {/* Games Tab */}
          {activeTab === "games" && (
            <div>
              <GamesSection
                isDarkMode={isDarkMode}
                userToken={userToken}
                API={API}
                showNotification={showNotification}
                hasPaidPlan={profileData?.plan && profileData.plan.name !== 'free' && profileData.plan.name !== 'Free Plan'}
                appSettings={appSettings}
                currentUser={profileData}
                onGameActive={onGameActive}
              />
            </div>
          )}
    </>
  );
};


// Cross-browser Flag Display Component

export default UserProfile;
